import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Toast from '../../components/Toast'
import RichTextEditor from '../../components/RichTextEditor'
import { useNotificationsSettingsContext } from '../../hooks/useNotificationsSettingsContext'
import { useBusinessSettingsContext } from '../../hooks/useBusinessSettingsContext'
import { normalizeWhatsAppPhoneAR } from '../../utils/whatsapp'
import './AdminNotificationsSettings.css'

const EVENT_LABELS = {
  welcome: 'Bienvenida',
  order_created: 'Compra registrada',
  payment_approved: 'Pago aprobado (pasarela)',
  order_delivered: 'Pedido entregado'
}

const PLACEHOLDER_HELP = [
  { key: '{{storeName}}', desc: 'Nombre comercial de la tienda.' },
  { key: '{{customerName}}', desc: 'Nombre completo del cliente.' },
  { key: '{{customerEmail}}', desc: 'Email del cliente.' },
  { key: '{{customerPhone}}', desc: 'Teléfono del cliente.' },
  { key: '{{orderNumber}}', desc: 'Número de pedido.' },
  { key: '{{orderTotal}}', desc: 'Total del pedido formateado.' },
  { key: '{{orderItemsHtml}}', desc: 'Listado de items en HTML.' },
  { key: '{{orderItemsText}}', desc: 'Listado de items en texto plano.' },
  { key: '{{paidVia}}', desc: 'Medio por el que se acreditó el pago.' },
  { key: '{{paymentMethod}}', desc: 'Método de pago elegido por el cliente.' },
  { key: '{{shippingCost}}', desc: 'Costo de envío formateado.' },
  { key: '{{shippingCostValue}}', desc: 'Costo de envío en valor numérico.' },
  { key: '{{shippingMethod}}', desc: 'Método de envío seleccionado.' },
  { key: '{{fulfillmentLabel}}', desc: 'Etiqueta de entrega mostrada al cliente.' },
  { key: '{{fulfillmentDetails}}', desc: 'Detalle completo de entrega o retiro.' },
  { key: '{{customerAddressLine}}', desc: 'Dirección completa en una línea.' },
  { key: '{{customerAddressStreet}}', desc: 'Calle y número del cliente.' },
  { key: '{{customerAddressApartment}}', desc: 'Departamento o piso del cliente.' },
  { key: '{{customerAddressCity}}', desc: 'Ciudad del cliente.' },
  { key: '{{customerAddressPostalCode}}', desc: 'Código postal del cliente.' },
  { key: '{{customerAddressProvince}}', desc: 'Provincia del cliente.' },
  { key: '{{customerAddressCountry}}', desc: 'País del cliente.' },
  { key: '{{pickupWarehouseName}}', desc: 'Nombre del depósito de retiro.' },
  { key: '{{pickupWarehouseAddress}}', desc: 'Dirección del depósito de retiro.' },
  { key: '{{orderNotes}}', desc: 'Notas del pedido.' },
  { key: '{{orderCreatedAt}}', desc: 'Fecha y hora de creación del pedido.' },
  { key: '{{orderCreatedDate}}', desc: 'Fecha de creación del pedido.' },
  { key: '{{orderCreatedTime}}', desc: 'Hora de creación del pedido.' },
  { key: '{{orderDeliveredAt}}', desc: 'Fecha y hora de entrega.' },
  { key: '{{orderDeliveredDate}}', desc: 'Fecha de entrega.' },
  { key: '{{orderDeliveredTime}}', desc: 'Hora de entrega.' },
  { key: '{{storeContactText}}', desc: 'Texto completo de contacto del negocio.' },
  { key: '{{storeContactAddress}}', desc: 'Dirección de contacto del negocio.' },
  { key: '{{storeContactPhone}}', desc: 'Teléfono de contacto del negocio.' },
  { key: '{{storeContactEmail}}', desc: 'Email de contacto del negocio.' },
  { key: '{{storeContactSchedule}}', desc: 'Horario de atención del negocio.' },
  { key: '{{storeContactWhatsapp}}', desc: 'WhatsApp de contacto del negocio.' },
  { key: '{{storeContactFacebook}}', desc: 'Facebook del negocio.' },
  { key: '{{storeContactInstagram}}', desc: 'Instagram del negocio.' },
  { key: '{{pdfUrl}}', desc: 'Link al PDF del comprobante.' }
]

const clone = (obj) => JSON.parse(JSON.stringify(obj || {}))

const setAt = (obj, path, value) => {
  const parts = Array.isArray(path) ? path : String(path).split('.').filter(Boolean)
  const out = Array.isArray(obj) ? obj.slice() : { ...(obj || {}) }
  let cur = out
  for (let i = 0; i < parts.length; i++) {
    const key = parts[i]
    if (i === parts.length - 1) {
      cur[key] = value
      break
    }
    const next = cur[key]
    cur[key] = next && typeof next === 'object' ? (Array.isArray(next) ? next.slice() : { ...next }) : {}
    cur = cur[key]
  }
  return out
}

const parseList = (raw) => {
  if (!raw) return []
  return String(raw)
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

function AdminNotificationsSettings() {
  const { settings, loading, error, update } = useNotificationsSettingsContext()
  const { settings: businessSettings } = useBusinessSettingsContext()
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  const [activeTab, setActiveTab] = useState('channels')
  const [activeEvent, setActiveEvent] = useState('order_created')
  const [scope, setScope] = useState('global')
  const [audience, setAudience] = useState('customer')
  const [extraEmailsText, setExtraEmailsText] = useState('')
  const [extraPhonesText, setExtraPhonesText] = useState('')

  const designId = businessSettings?.active?.designId || 'muebles/v1'

  useEffect(() => {
    if (!settings) return
    setDraft(clone(settings))
    const extraEmails = Array.isArray(settings?.internalRecipients?.extraEmails) ? settings.internalRecipients.extraEmails : []
    const extraPhones = Array.isArray(settings?.internalRecipients?.extraPhones) ? settings.internalRecipients.extraPhones : []
    setExtraEmailsText(extraEmails.join(', '))
    setExtraPhonesText(extraPhones.join(', '))
  }, [settings])

  const overrideRoot = useMemo(() => {
    if (!draft) return null
    if (scope !== 'design') return null
    const root = draft.designOverrides && typeof draft.designOverrides === 'object' ? draft.designOverrides : {}
    const current = root[designId] && typeof root[designId] === 'object' ? root[designId] : {}
    return current
  }, [draft, scope, designId])

  const effectiveTemplate = useMemo(() => {
    if (!draft) return null
    const base = draft.templates?.[activeEvent] || null
    if (!base) return null
    const baseTarget = base?.[audience] || base
    const resolveFromOverride = (override) => {
      if (!override) return baseTarget
      const legacyOverride = override && (override.email || override.whatsapp) ? override : null
      const overrideTarget = override?.[audience] || legacyOverride
      if (!overrideTarget) return baseTarget
      return {
        email: {
          subject: overrideTarget?.email?.subject ?? baseTarget?.email?.subject,
          html: overrideTarget?.email?.html ?? baseTarget?.email?.html
        },
        whatsapp: {
          message: overrideTarget?.whatsapp?.message ?? baseTarget?.whatsapp?.message
        }
      }
    }
    if (scope !== 'design') return resolveFromOverride(null)
    const override = overrideRoot?.templates?.[activeEvent] || null
    return resolveFromOverride(override)
  }, [draft, activeEvent, scope, overrideRoot, audience])

  const effectiveTheme = useMemo(() => {
    if (!draft) return null
    if (scope !== 'design') return null
    const theme = overrideRoot?.theme && typeof overrideRoot.theme === 'object' ? overrideRoot.theme : {}
    return {
      storeName: theme.storeName || '',
      logoUrl: theme.logoUrl || '',
      primaryColor: theme.primaryColor || ''
    }
  }, [draft, scope, overrideRoot])

  const setDraftValue = (path, value) => setDraft((prev) => setAt(prev, path, value))

  const setTemplateValue = (channel, field, value) => {
    if (!draft) return
    if (scope === 'global') {
      setDraftValue(['templates', activeEvent, audience, channel, field], value)
      return
    }
    const root = draft.designOverrides && typeof draft.designOverrides === 'object' ? draft.designOverrides : {}
    const current = root[designId] && typeof root[designId] === 'object' ? root[designId] : {}
    const next = setAt(current, ['templates', activeEvent, audience, channel, field], value)
    setDraftValue(['designOverrides', designId], next)
  }

  const setThemeValue = (field, value) => {
    if (!draft) return
    const root = draft.designOverrides && typeof draft.designOverrides === 'object' ? draft.designOverrides : {}
    const current = root[designId] && typeof root[designId] === 'object' ? root[designId] : {}
    const next = setAt(current, ['theme', field], value)
    setDraftValue(['designOverrides', designId], next)
  }

  const validateEmailProvider = () => {
    if (!draft?.channels?.email?.enabled) return { ok: true }
    const provider = draft?.channels?.email?.provider || 'resend'

    if (provider === 'sendpulse') {
      const clientId = (draft?.channels?.email?.sendpulse?.clientId || '').trim()
      const clientSecret = (draft?.channels?.email?.sendpulse?.clientSecret || '').trim()
      if (!clientId) return { ok: false, message: 'El Client ID de SendPulse es requerido' }
      if (!clientSecret) return { ok: false, message: 'El Client Secret de SendPulse es requerido' }
      return { ok: true }
    }

    if (provider === 'sender') {
      const apiToken = (draft?.channels?.email?.sender?.apiToken || '').trim()
      const baseUrl = (draft?.channels?.email?.sender?.baseUrl || '').trim()
      const endpoint = (draft?.channels?.email?.sender?.endpoint || '').trim()
      if (!apiToken) return { ok: false, message: 'El API Token de Sender es requerido' }
      if (!baseUrl) return { ok: false, message: 'La Base URL de Sender es requerida' }
      if (!endpoint) return { ok: false, message: 'El Endpoint de Sender es requerido' }
      return { ok: true }
    }

    return { ok: true }
  }

  const validateWhatsAppSender = () => {
    if (!draft?.channels?.whatsapp?.enabled) return { ok: true }
    const provider = draft?.channels?.whatsapp?.provider || 'sendmessagecloud'

    if (provider === 'ultramsg') {
      const instanceId = (draft?.channels?.whatsapp?.ultramsg?.instanceId || '').trim()
      const token = (draft?.channels?.whatsapp?.ultramsg?.token || '').trim()
      if (!instanceId) return { ok: false, message: 'El Instance ID de Ultramsg es requerido' }
      if (!token) return { ok: false, message: 'El Token de Ultramsg es requerido' }
      return { ok: true }
    }

    if (provider === 'respondio') {
      const apiToken = (draft?.channels?.whatsapp?.respondio?.apiToken || '').trim()
      if (!apiToken) return { ok: false, message: 'El API Token de Respond.io es requerido' }
      return { ok: true }
    }

    const senderPhone = draft?.channels?.whatsapp?.sendmessagecloud?.senderPhone || ''
    const norm = normalizeWhatsAppPhoneAR(senderPhone)
    if (!norm.isValid) return { ok: false, message: 'El teléfono origen debe estar en formato 549XXXXXXXXXX' }
    const senderUid = (draft?.channels?.whatsapp?.sendmessagecloud?.senderUid || '').trim()
    if (!senderUid) return { ok: false, message: 'El UID (token) de SendMessageCloud es requerido' }
    return { ok: true }
  }

  const handleSave = async () => {
    if (!draft || saving) return
    const emailValidation = validateEmailProvider()
    if (!emailValidation.ok) {
      setNotification({ type: 'error', message: emailValidation.message })
      setTimeout(() => setNotification(null), 3500)
      return
    }
    const waValidation = validateWhatsAppSender()
    if (!waValidation.ok) {
      setNotification({ type: 'error', message: waValidation.message })
      setTimeout(() => setNotification(null), 3500)
      return
    }
    const normalizedDraft = setAt(
      setAt(draft, ['internalRecipients', 'extraEmails'], parseList(extraEmailsText)),
      ['internalRecipients', 'extraPhones'],
      parseList(extraPhonesText)
    )
    setDraft(normalizedDraft)
    setSaving(true)
    try {
      await update(normalizedDraft)
      setNotification({ type: 'success', message: 'Configuración de notificaciones guardada' })
      setTimeout(() => setNotification(null), 2500)
    } catch (_e) {
      setNotification({ type: 'error', message: 'Error guardando la configuración de notificaciones' })
      setTimeout(() => setNotification(null), 3500)
    } finally {
      setSaving(false)
    }
  }

  if (loading || !draft) {
    return (
      <div className="admin-notifications-settings-page">
        <div className="loading-state">
          <p>Cargando configuración...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-notifications-settings-page">
        <div className="error-state">
          <p>{error}</p>
        </div>
      </div>
    )
  }

  const emailProvider = draft.channels?.email?.provider || 'resend'
  const whatsappProvider = draft.channels?.whatsapp?.provider || 'sendmessagecloud'
  const senderPhonePreview = normalizeWhatsAppPhoneAR(draft.channels?.whatsapp?.sendmessagecloud?.senderPhone || '')
  const invalidExtraPhones = parseList(extraPhonesText).filter((p) => !normalizeWhatsAppPhoneAR(p).isValid)

  return (
    <div className="admin-notifications-settings-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="notifications-settings-header">
        <div>
          <Link to="/admin/configuraciones">
            <button className="back-button">← Volver</button>
          </Link>
          <h1>Notificaciones</h1>
          <p className="notifications-settings-subtitle">
            Configura Email (Resend/SendPulse/Sender) y WhatsApp (SendMessageCloud/Respond.io/Ultramsg) para eventos del sistema.
          </p>
        </div>
        <button
          type="button"
          className="notifications-save-button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>

      <div className="notifications-layout">
        <div className="notifications-tabs">
          <button
            type="button"
            className={`notifications-tab ${activeTab === 'channels' ? 'active' : ''}`}
            onClick={() => setActiveTab('channels')}
          >
            Canales y destinatarios
          </button>
          <button
            type="button"
            className={`notifications-tab ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => setActiveTab('templates')}
          >
            Plantillas
          </button>
        </div>

        <div className="notifications-content">
          {activeTab === 'channels' ? (
            <div className="notifications-grid">
              <div className="notifications-card">
                <h2>Canales</h2>

            <div className="ns-section">
              <h3>Email</h3>
              <label className="ns-checkbox">
                <input
                  type="checkbox"
                  checked={draft.channels?.email?.enabled !== false}
                  onChange={(e) => setDraftValue(['channels', 'email', 'enabled'], e.target.checked)}
                />
                <span>Habilitar envío por Email</span>
              </label>
              <div className="ns-row">
                <div className="ns-field">
                  <label>Proveedor</label>
                  <select
                    value={emailProvider}
                    onChange={(e) => setDraftValue(['channels', 'email', 'provider'], e.target.value)}
                  >
                    <option value="resend">Resend</option>
                    <option value="sendpulse">SendPulse</option>
                    <option value="sender">Sender</option>
                  </select>
                </div>
              </div>
              <div className="ns-row">
                <div className="ns-field">
                  <label>Nombre remitente</label>
                  <input
                    value={draft.channels?.email?.fromName || ''}
                    onChange={(e) => setDraftValue(['channels', 'email', 'fromName'], e.target.value)}
                    placeholder="PinoHome"
                  />
                </div>
                <div className="ns-field">
                  <label>Email remitente</label>
                  <input
                    value={draft.channels?.email?.fromEmail || ''}
                    onChange={(e) => setDraftValue(['channels', 'email', 'fromEmail'], e.target.value)}
                    placeholder="no-reply@tudominio.com"
                  />
                </div>
              </div>
              <div className="ns-row">
                <div className="ns-field">
                  <label>Reply-To</label>
                  <input
                    value={draft.channels?.email?.replyTo || ''}
                    onChange={(e) => setDraftValue(['channels', 'email', 'replyTo'], e.target.value)}
                    placeholder="soporte@tudominio.com"
                  />
                </div>
              </div>
              {emailProvider === 'resend' ? (
                <div className="ns-row">
                  <div className="ns-field">
                    <label>API Key</label>
                    <input
                      type="password"
                      value={draft.channels?.email?.resend?.apiKey || ''}
                      onChange={(e) => setDraftValue(['channels', 'email', 'resend', 'apiKey'], e.target.value)}
                      placeholder="re_XXXXXXXXXXXXXXXX"
                    />
                  </div>
                </div>
              ) : null}
              {emailProvider === 'sendpulse' ? (
                <div className="ns-row">
                  <div className="ns-field">
                    <label>Client ID</label>
                    <input
                      value={draft.channels?.email?.sendpulse?.clientId || ''}
                      onChange={(e) => setDraftValue(['channels', 'email', 'sendpulse', 'clientId'], e.target.value)}
                      placeholder="SendPulse Client ID"
                    />
                  </div>
                  <div className="ns-field">
                    <label>Client Secret</label>
                    <input
                      type="password"
                      value={draft.channels?.email?.sendpulse?.clientSecret || ''}
                      onChange={(e) => setDraftValue(['channels', 'email', 'sendpulse', 'clientSecret'], e.target.value)}
                      placeholder="SendPulse Client Secret"
                    />
                  </div>
                </div>
              ) : null}
              {emailProvider === 'sender' ? (
                <div className="ns-row">
                  <div className="ns-field">
                    <label>API Token</label>
                    <input
                      type="password"
                      value={draft.channels?.email?.sender?.apiToken || ''}
                      onChange={(e) => setDraftValue(['channels', 'email', 'sender', 'apiToken'], e.target.value)}
                      placeholder="sender_live_XXXXXXXXXXXXXXXX"
                    />
                  </div>
                  <div className="ns-field">
                    <label>Base URL</label>
                    <input
                      value={draft.channels?.email?.sender?.baseUrl || ''}
                      onChange={(e) => setDraftValue(['channels', 'email', 'sender', 'baseUrl'], e.target.value)}
                      placeholder="https://api.sender.net/v2"
                    />
                  </div>
                </div>
              ) : null}
              {emailProvider === 'sender' ? (
                <div className="ns-row">
                  <div className="ns-field">
                    <label>Endpoint</label>
                    <input
                      value={draft.channels?.email?.sender?.endpoint || ''}
                      onChange={(e) => setDraftValue(['channels', 'email', 'sender', 'endpoint'], e.target.value)}
                      placeholder="/transactional/emails"
                    />
                  </div>
                </div>
              ) : null}
              {emailProvider === 'resend' ? (
                <div className="ns-hint">
                  Si no cargas API Key de Resend, se usa RESEND_API_KEY en Cloudflare.
                </div>
              ) : null}
            </div>

            <div className="ns-section">
              <h3>WhatsApp</h3>
              <label className="ns-checkbox">
                <input
                  type="checkbox"
                  checked={draft.channels?.whatsapp?.enabled === true}
                  onChange={(e) => setDraftValue(['channels', 'whatsapp', 'enabled'], e.target.checked)}
                />
                <span>Habilitar envío por WhatsApp</span>
              </label>
              <div className="ns-row">
                <div className="ns-field">
                  <label>Proveedor</label>
                  <select
                    value={whatsappProvider}
                    onChange={(e) => setDraftValue(['channels', 'whatsapp', 'provider'], e.target.value)}
                  >
                    <option value="sendmessagecloud">SendMessageCloud</option>
                    <option value="respondio">Respond.io</option>
                    <option value="ultramsg">Ultramsg</option>
                  </select>
                </div>
              </div>
              {whatsappProvider === 'sendmessagecloud' ? (
                <div className="ns-row">
                  <div className="ns-field">
                    <label>UID (token)</label>
                    <input
                      value={draft.channels?.whatsapp?.sendmessagecloud?.senderUid || ''}
                      onChange={(e) => setDraftValue(['channels', 'whatsapp', 'sendmessagecloud', 'senderUid'], e.target.value)}
                      placeholder="UID del usuario en SendMessageCloud"
                    />
                  </div>
                  <div className="ns-field">
                    <label>Número origen</label>
                    <input
                      value={draft.channels?.whatsapp?.sendmessagecloud?.senderPhone || ''}
                      onChange={(e) => setDraftValue(['channels', 'whatsapp', 'sendmessagecloud', 'senderPhone'], e.target.value)}
                      placeholder="5493813333333"
                    />
                  </div>
                </div>
              ) : null}
              {whatsappProvider === 'respondio' ? (
                <div className="ns-row">
                  <div className="ns-field">
                    <label>Base URL</label>
                    <input
                      value={draft.channels?.whatsapp?.respondio?.baseUrl || ''}
                      onChange={(e) => setDraftValue(['channels', 'whatsapp', 'respondio', 'baseUrl'], e.target.value)}
                      placeholder="https://api.respond.io/v2"
                    />
                  </div>
                  <div className="ns-field">
                    <label>API Token</label>
                    <input
                      type="password"
                      value={draft.channels?.whatsapp?.respondio?.apiToken || ''}
                      onChange={(e) => setDraftValue(['channels', 'whatsapp', 'respondio', 'apiToken'], e.target.value)}
                      placeholder="Token de Respond.io"
                    />
                  </div>
                </div>
              ) : null}
              {whatsappProvider === 'ultramsg' ? (
                <div className="ns-row">
                  <div className="ns-field">
                    <label>Instance ID</label>
                    <input
                      value={draft.channels?.whatsapp?.ultramsg?.instanceId || ''}
                      onChange={(e) => setDraftValue(['channels', 'whatsapp', 'ultramsg', 'instanceId'], e.target.value)}
                      placeholder="instance12345"
                    />
                  </div>
                  <div className="ns-field">
                    <label>Token</label>
                    <input
                      type="password"
                      value={draft.channels?.whatsapp?.ultramsg?.token || ''}
                      onChange={(e) => setDraftValue(['channels', 'whatsapp', 'ultramsg', 'token'], e.target.value)}
                      placeholder="Ultramsg Token"
                    />
                  </div>
                </div>
              ) : null}
              {draft.channels?.whatsapp?.enabled && whatsappProvider === 'sendmessagecloud' ? (
                <div className={`ns-hint ${senderPhonePreview.isValid ? 'ok' : 'bad'}`}>
                  {senderPhonePreview.isValid
                    ? `Formato OK: ${senderPhonePreview.normalized}`
                    : 'El número origen debe estar en formato 549XXXXXXXXXX'}
                </div>
              ) : null}
            </div>
          </div>

          <div className="notifications-card">
            <h2>Destinatarios internos</h2>

            <div className="ns-section">
              <h3>Por rol</h3>
              <div className="ns-pill-row">
                {['admin', 'superadmin'].map((role) => {
                  const roles = Array.isArray(draft.internalRecipients?.roles) ? draft.internalRecipients.roles : []
                  const checked = roles.includes(role)
                  return (
                    <button
                      key={role}
                      type="button"
                      className={`ns-pill ${checked ? 'selected' : ''}`}
                      onClick={() => {
                        const next = checked ? roles.filter((r) => r !== role) : [...roles, role]
                        setDraftValue(['internalRecipients', 'roles'], next)
                      }}
                    >
                      {role}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="ns-section">
              <h3>Extras</h3>
              <div className="ns-field">
                <label>Emails (separados por coma)</label>
                <input
                  value={extraEmailsText}
                  onChange={(e) => setExtraEmailsText(e.target.value)}
                  onBlur={() => setDraftValue(['internalRecipients', 'extraEmails'], parseList(extraEmailsText))}
                  placeholder="admin@tudominio.com, ventas@tudominio.com"
                />
              </div>
              <div className="ns-field">
                <label>Teléfonos WhatsApp (separados por coma)</label>
                <input
                  value={extraPhonesText}
                  onChange={(e) => setExtraPhonesText(e.target.value)}
                  onBlur={() => setDraftValue(['internalRecipients', 'extraPhones'], parseList(extraPhonesText))}
                  placeholder="5493813333333, 54911XXXXXXXXX"
                />
                {invalidExtraPhones.length ? (
                  <div className="ns-hint bad">
                    Teléfonos inválidos (no se enviará WhatsApp): {invalidExtraPhones.join(', ')}
                  </div>
                ) : (
                  <div className="ns-hint">
                    Solo se enviará WhatsApp a números válidos (549XXXXXXXXXX).
                  </div>
                )}
              </div>
            </div>
              </div>
            </div>
          ) : null}

          {activeTab === 'templates' ? (
            <div className="notifications-grid">
              <div className="notifications-card wide">
                <h2>Plantillas</h2>

            <div className="ns-section">
              <div className="ns-row ns-row-tight">
                <div className="ns-field">
                  <label>Evento</label>
                  <select value={activeEvent} onChange={(e) => setActiveEvent(e.target.value)}>
                    {Object.keys(EVENT_LABELS).map((k) => (
                      <option key={k} value={k}>{EVENT_LABELS[k]}</option>
                    ))}
                  </select>
                </div>
                <div className="ns-field">
                  <label>Ámbito</label>
                  <select value={scope} onChange={(e) => setScope(e.target.value)}>
                    <option value="global">Global</option>
                    <option value="design">Por diseño actual ({designId})</option>
                  </select>
                </div>
                <div className="ns-field">
                  <label>Destinatario</label>
                  <select value={audience} onChange={(e) => setAudience(e.target.value)}>
                    <option value="customer">Cliente</option>
                    <option value="internal">Interno</option>
                  </select>
                </div>
              </div>

              {scope === 'design' ? (
                <div className="ns-theme">
                  <h3>Diseño (override)</h3>
                  <div className="ns-row">
                    <div className="ns-field">
                      <label>Nombre comercial</label>
                      <input
                        value={effectiveTheme?.storeName || ''}
                        onChange={(e) => setThemeValue('storeName', e.target.value)}
                        placeholder="PinoHome"
                      />
                    </div>
                    <div className="ns-field">
                      <label>Color principal</label>
                      <input
                        value={effectiveTheme?.primaryColor || ''}
                        onChange={(e) => setThemeValue('primaryColor', e.target.value)}
                        placeholder="#8b5a2b"
                      />
                    </div>
                  </div>
                  <div className="ns-row">
                    <div className="ns-field">
                      <label>Logo URL</label>
                      <input
                        value={effectiveTheme?.logoUrl || ''}
                        onChange={(e) => setThemeValue('logoUrl', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="ns-template-grid">
                <div className="ns-template-col">
                  <h3>Email</h3>
                  <div className="ns-field">
                    <label>Asunto</label>
                    <input
                      value={effectiveTemplate?.email?.subject || ''}
                      onChange={(e) => setTemplateValue('email', 'subject', e.target.value)}
                    />
                  </div>
                  <div className="ns-field">
                    <label>Cuerpo (HTML)</label>
                    <RichTextEditor
                      value={effectiveTemplate?.email?.html || ''}
                      onChange={(v) => setTemplateValue('email', 'html', v)}
                      placeholder="Escribe el contenido del email..."
                    />
                  </div>
                </div>

                <div className="ns-template-col">
                  <h3>WhatsApp</h3>
                  <div className="ns-field">
                    <label>Mensaje</label>
                    <textarea
                      value={effectiveTemplate?.whatsapp?.message || ''}
                      onChange={(e) => setTemplateValue('whatsapp', 'message', e.target.value)}
                      rows={10}
                    />
                    <div className="ns-hint">
                      Usá los placeholders disponibles para insertar datos del pedido.
                    </div>
                  </div>
                </div>
              </div>

              <div className="ns-placeholder-card">
                <h3>Placeholders disponibles</h3>
                <div className="ns-placeholder-grid">
                  {PLACEHOLDER_HELP.map((item) => (
                    <div key={item.key} className="ns-placeholder-item">
                      <span className="ns-placeholder-key">{item.key}</span>
                      <span className="ns-placeholder-desc">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default AdminNotificationsSettings
